/**
 * API Configuration Utility
 * Reads REACT_APP_API_URL from environment variables (.env, .env.local, .env.production, etc.)
 * Respects React's environment variable priority:
 * 1. .env.development.local / .env.production.local (highest priority)
 * 2. .env.local (always loaded except test)
 * 3. .env.development / .env.production (based on NODE_ENV)
 * 4. .env (lowest priority)
 * 
 * DO NOT EDIT THIS FILE - Configure REACT_APP_API_URL in your .env files instead
 */

const getApiBaseUrl = () => {
  // Read directly from environment variable (set in .env files)
  // This will automatically use the correct value based on React's env file priority
  const apiUrl = process.env.REACT_APP_API_URL;
  
  // Always log what we're reading from the environment (helps debug which .env file is being used)
  console.log('[apiConfig] Reading REACT_APP_API_URL from environment...');
  console.log('[apiConfig] NODE_ENV:', process.env.NODE_ENV);
  console.log('[apiConfig] REACT_APP_API_URL from env:', apiUrl || 'NOT SET');
  
  if (!apiUrl) {
    // If REACT_APP_API_URL is not set, throw an error to make it clear
    const errorMsg = '[apiConfig] ❌ ERROR: REACT_APP_API_URL is not set in environment variables!\n' +
      'Please set REACT_APP_API_URL in your .env file.\n' +
      'For local development: REACT_APP_API_URL=http://localhost:5000/api\n' +
      'For production: REACT_APP_API_URL=https://your-backend-url.com/api\n' +
      'Make sure to include /api at the end!';
    
    console.error(errorMsg);
    
    // In production, we should fail fast rather than use a hardcoded default
    // This ensures the .env file is properly configured
    throw new Error('REACT_APP_API_URL is not configured. Please set it in your .env file.');
  }
  
  console.log('[apiConfig] ✓ Using REACT_APP_API_URL from .env file:', apiUrl);
  
  // Normalize the URL: remove trailing slashes and ensure it ends with /api
  let normalizedUrl = apiUrl.trim();
  
  // Remove all trailing slashes
  normalizedUrl = normalizedUrl.replace(/\/+$/, '');
  
  // If it doesn't end with /api, add it
  if (!normalizedUrl.endsWith('/api')) {
    // Check if it ends with just 'api' (without leading slash)
    if (normalizedUrl.endsWith('api')) {
      // Already has 'api' but might be missing leading slash
      normalizedUrl = normalizedUrl.replace(/api$/, '/api');
    } else {
      // Add /api suffix
      normalizedUrl = normalizedUrl + '/api';
    }
    
    console.warn(`[apiConfig] ⚠️ REACT_APP_API_URL was missing /api suffix. Adjusted from "${apiUrl}" to "${normalizedUrl}"`);
    console.warn('[apiConfig] Please update your .env file to include /api: REACT_APP_API_URL=https://your-backend-url.com/api');
  }
  
  // Final check: ensure no trailing slash (for safe path concatenation)
  normalizedUrl = normalizedUrl.replace(/\/+$/, '');
  
  // Log final URL being used
  console.log('[apiConfig] Final API_BASE_URL:', normalizedUrl);
  console.log('[apiConfig] ✅ API configuration loaded successfully from .env file');
  
  return normalizedUrl;
};

// Export the validated API base URL
// This reads from REACT_APP_API_URL in your .env file
export const API_BASE_URL = getApiBaseUrl();

// Log when module loads to confirm configuration
console.log('[apiConfig] Module loaded - Using API_BASE_URL:', API_BASE_URL);
console.log('[apiConfig] This value comes from REACT_APP_API_URL in your .env file');

// Export the function for dynamic use if needed
export default getApiBaseUrl;
